import {
  BaseProps,
  PopoverProps,
  defaultThemeProp,
  Text,
  ForwardProps,
  useUID,
  useElement,
  useConsolidatedRef,
  usePrevious,
  useOuterEvent,
  getFocusables,
  Progress,
  Popover,
  CardHeader,
  Button,
  Icon,
  VisuallyHiddenText,
  CardContent,
  CardFooter,
} from '@pega/cosmos-react-core';
import React from 'react';
import {
  forwardRef,
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import styled, { css } from 'styled-components';
import useEscape from '../../hooks/useEscape';
import useFocusTrap from '../../hooks/useFocusTrap';
import useLocalTranslation from '../../hooks/useLocalTranslation';

export interface DialogProps extends BaseProps {
  target: HTMLElement | null;
  placement?: PopoverProps['placement'];
  children: PopoverProps['children'];
  heading: string;
  headingHidden?: boolean;
  onSubmit?: (arg: { close: () => void }) => void;
  onClose?: () => void;
  onOpen?: () => void;
  loading?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export const StyledDialog = styled.dialog(
  ({
    theme: {
      base: {
        palette: { 'foreground-color': foregroundColor },
      },
    },
  }) => {
    return css`
      border: none;
      color: ${foregroundColor};
    `;
  },
);

StyledDialog.defaultProps = defaultThemeProp;

export const StyledDialogTitle = styled(Text)(
  ({
    theme: {
      base: { shadow, 'border-radius': borderRadius },
    },
  }) => {
    return css`
      position: relative;
      ::after {
        content: '';
        border-radius: ${borderRadius};
        position: absolute;
        inset: -0.25rem;
      }
      :focus-visible {
        outline: none;
        ::after {
          box-shadow: ${shadow.focus}, inset ${shadow['focus']};
        }
      }
    `;
  },
);

StyledDialogTitle.defaultProps = defaultThemeProp;

export const useDialog = () => {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);

  const setTargetWithEvent = (e: ReactMouseEvent | ReactKeyboardEvent) => {
    if (e.target instanceof HTMLElement) {
      setTarget(e.target);
    }
  };

  return {
    target,
    loading,
    setLoading,
    events: {
      onMouseDown: (e: ReactMouseEvent) => {
        setTargetWithEvent(e);
      },
      onKeyDown: (e: ReactKeyboardEvent) => {
        if (e.key === 'Enter') {
          setTargetWithEvent(e);
        }
      },
    },
    setTargetWithEvent,
  };
};

const Dialog: FunctionComponent<DialogProps & ForwardProps> = forwardRef(
  (
    {
      target,
      placement,
      heading,
      headingHidden = false,
      onSubmit,
      onClose,
      onOpen,
      loading,
      children,
      ...restProps
    }: PropsWithoutRef<DialogProps>,
    ref: DialogProps['ref'],
  ) => {
    const headingId = useUID();
    const [popoverEl, setPopoverEl] = useElement<HTMLDivElement>();
    const popoverRef = useConsolidatedRef(ref, setPopoverEl);
    const headingRef = useRef<HTMLSpanElement>(null);
    const [currentTarget, setCurrentTarget] = useState(() => target);
    const [show, setShow] = useState(false);
    const skipChangeShow = useRef(false);
    const prevTarget = usePrevious(target);
    const translate = useLocalTranslation();

    useEscape(
      () => {
        if (show) {
          setShow(false);
        }
      },
      undefined,
      [show, setShow],
    );
    useOuterEvent('mousedown', [target, popoverEl], () => {
      if (target && popoverEl && show) {
        setShow(false);
        onClose?.();
      }
    });
    useFocusTrap(popoverRef);

    useEffect(() => {
      if (!show) {
        currentTarget?.focus();
      }
    }, [show, currentTarget]);

    useEffect(() => {
      if (show && popoverEl) {
        if (headingRef.current) {
          headingRef.current.focus();
        } else {
          const focusables = getFocusables(popoverRef);
          focusables[0]?.focus();
        }
      }
    }, [show, popoverEl, popoverRef]);

    useLayoutEffect(() => {
      const onTargetClick = (e: MouseEvent) => {
        setCurrentTarget((prev) => (e.target ? (e.target as HTMLElement) : prev));
        if (!skipChangeShow.current) {
          setShow((prev) => !prev);
        } else {
          skipChangeShow.current = false;
        }
      };
      if (target instanceof HTMLElement) {
        target.addEventListener('click', onTargetClick);
      }
      return () => {
        if (target instanceof HTMLElement) {
          target.removeEventListener('click', onTargetClick);
        }
      };
    }, [target]);

    useEffect(() => {
      if (show) {
        onOpen?.();
      } else {
        onClose?.();
      }
    }, [show, onOpen, onClose]);

    useEffect(() => {
      /* If our target is changing and the popover is already open we will want
      to skip the next change. The next show change would come from a click on the new button,
      the popover should stay open. */
      if (target !== prevTarget && show) {
        skipChangeShow.current = true;
      }
    }, [target, show, prevTarget]);

    const onTitleKeyDown = (e: ReactKeyboardEvent) => {
      const focusables = getFocusables(popoverRef);
      if (e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        focusables[focusables.length - 1].focus();
      }
    };

    const progress = loading ? <Progress placement="local" /> : null;

    return (
      <Popover
        {...restProps}
        as={StyledDialog}
        ref={popoverRef}
        target={currentTarget}
        placement={placement}
        show={show}
        open={show}
        aria-labelledby={headingId}
      >
        <CardHeader
          actions={
            !onSubmit ? (
              <Button
                icon
                label={translate('close')}
                variant="simple"
                onClick={() => setShow(false)}
                aria-label={translate('close')}
              >
                <Icon name="times" />
              </Button>
            ) : undefined
          }
        >
          {headingHidden ? (
            <VisuallyHiddenText id={headingId}>{heading}</VisuallyHiddenText>
          ) : (
            <StyledDialogTitle variant="h2" tabIndex={-1} ref={headingRef} onKeyDown={onTitleKeyDown} id={headingId}>
              {heading}
            </StyledDialogTitle>
          )}
        </CardHeader>
        <CardContent>
          {children}
          {progress}
        </CardContent>
        {onSubmit && (
          <CardFooter justify="between">
            <Button variant="secondary" onClick={() => setShow(false)}>
              {translate('cancel')}
            </Button>
            <Button variant="primary" onClick={() => onSubmit({ close: () => setShow(false) })} disabled={loading}>
              {translate('submit')}
            </Button>
          </CardFooter>
        )}
      </Popover>
    );
  },
);

export default Dialog;
